// Interior tutorial (first entry into one's own house) — pure decisions.
//
// It runs in a practice room that exists only in the browser (PRACTICE_ROOM_ID):
// a bought house may still hold the previous owner's furniture, and practice
// purchases cost nothing. The server only stores whether the tutorial is over
// (onboarding KV `interior_onboarding` = done / skipped); the step lives in
// memory, and inside an item step the phase — buy it, place it — is read from
// the practice room's furniture.

export const INTERIOR_TUTORIAL_STEPS = ['build', 'walls', 'floor', 'bed', 'flip', 'table', 'bouquet', 'finish'] as const;
export type TutorialStep = (typeof INTERIOR_TUTORIAL_STEPS)[number];
/** Stored when the tutorial is over (finished or skipped). */
export const TUTORIAL_DONE = 'done';
export const TUTORIAL_SKIPPED = 'skipped';

export type TutorialItemStep = 'walls' | 'floor' | 'bed' | 'table' | 'bouquet';

/** The five practice purchases the tutorial walks through. */
export const TUTORIAL_ITEM: Record<TutorialItemStep, number> = {
  walls: 108, // Soft Marble Plaster Wall
  floor: 111, // Light Oak Flooring
  bed: 23, // Blush Paradise Bed
  table: 30, // Royal Blush Dining Table (pink)
  bouquet: 99, // Pink Tulip Bouquet (the blue rose dome is Stars-only)
};

/** The shop tab each item lives in. */
export const TUTORIAL_ITEM_CATEGORY: Record<TutorialItemStep, string> = {
  walls: 'walls',
  floor: 'floors',
  bed: 'beds',
  table: 'tables',
  bouquet: 'decor',
};

export const isItemStep = (step: TutorialStep | null): step is TutorialItemStep =>
  step === 'walls' || step === 'floor' || step === 'bed' || step === 'table' || step === 'bouquet';

/** Has the player still to see the tutorial? (Nothing stored, or an old in-progress step.) */
export function tutorialPending(stored: string | null | undefined): boolean {
  return stored !== TUTORIAL_DONE && stored !== TUTORIAL_SKIPPED;
}

export function nextStep(step: TutorialStep): TutorialStep | typeof TUTORIAL_DONE {
  const i = INTERIOR_TUTORIAL_STEPS.indexOf(step);
  return i < INTERIOR_TUTORIAL_STEPS.length - 1 ? INTERIOR_TUTORIAL_STEPS[i + 1] : TUTORIAL_DONE;
}

export type ItemPhase = 'buy' | 'place';

interface OwnedItem {
  furniture_catalog_id: number;
  is_placed: boolean;
}

/** Where the player is with an item: not in the room yet → buy; in the room → place. */
export function itemPhase(items: readonly OwnedItem[], catalogId: number): ItemPhase {
  return items.some((i) => i.furniture_catalog_id === catalogId && i.is_placed) ? 'place' : 'buy';
}

// ── Practice room ──

/** The browser-only room the tutorial runs in. Never sent to the server. */
export const PRACTICE_ROOM_ID = -1;
export const isPracticeRoom = (mapObjectId: number | null | undefined): boolean => mapObjectId === PRACTICE_ROOM_ID;

/** Where a bought item appears — the server's default spot (interior/service.py buy_furniture). */
export const PRACTICE_SPAWN = { x: -3.3333333333333286, y: 214.44444444444449 };

interface PracticeCatalogItem {
  id: number;
  category: string;
}

interface PracticeItem extends OwnedItem {
  id: number;
  furniture_catalog: PracticeCatalogItem;
}

/**
 * The practice room after buying `catalog` as instance `id`: it appears at the
 * spawn spot, placed; a wall or floor covering replaces the previous one (as on
 * the server).
 */
export function addPracticePurchase<T extends PracticeItem>(items: readonly T[], item: T): T[] {
  const cat = item.furniture_catalog.category;
  const replaces = cat === 'walls' || cat === 'floors';
  const kept = replaces ? items.filter((i) => i.furniture_catalog.category !== cat) : items;
  return [...kept, item];
}

/** A target spot: the item's anchor (front) tile and the footprint it covers behind it. */
export interface TutorialTarget {
  tileX: number;
  tileY: number;
  /** Footprint size behind the anchor (tiles) — what the floor highlight shows. */
  w: number;
  h: number;
}

/**
 * Bed against the left wall towards the front; the pink table beside it, further
 * along the same wall. Both well away from where bought items appear (the room
 * centre, ~tile 17,17) and from the starter bed there, so the drag is a real one.
 */
export const TUTORIAL_TARGETS: Record<'bed' | 'table', TutorialTarget> = {
  bed: { tileX: 13, tileY: 30, w: 12, h: 13 },
  table: { tileX: 10, tileY: 37, w: 9, h: 5 },
};

/** How far (tiles) the anchor may land from the target and still count — floor items move freely. */
export const TARGET_TOLERANCE_TILES = 3;

export function inTarget(anchor: { x: number; y: number }, target: TutorialTarget): boolean {
  return Math.abs(anchor.x - target.tileX) <= TARGET_TOLERANCE_TILES
    && Math.abs(anchor.y - target.tileY) <= TARGET_TOLERANCE_TILES;
}

/** A step with no progress for this long offers "Skip step" (never a dead end). */
export const STUCK_AFTER_MS = 90_000;
